import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function Repairs() {
  const [repairs, setRepairs] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      const [r, v] = await Promise.all([api.get("/repairs"), api.get("/vehicles")]);
      setRepairs(r.data);
      setVehicles(Object.fromEntries(v.data.map((x) => [x.id, x])));
    })();
  }, []);

  const filtered = repairs.filter((r) => {
    const v = vehicles[r.vehicle_id];
    const blob = `${r.repair_type} ${r.description} ${r.vendor} ${r.mechanic} ${v ? v.make + " " + v.model + " " + v.year + " " + v.vin : ""}`.toLowerCase();
    return blob.includes(q.toLowerCase());
  });
  const total = filtered.reduce((s, r) => s + (r.parts_cost + r.labor_cost + r.tax), 0);

  return (
    <div data-testid="repairs-page">
      <PageHeader overline="Service" title="Repairs Log" description="Cross-fleet repair history with parts/labor/tax breakdown." />
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="repairs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search type, vendor, mechanic, vehicle…" className="pl-9 rounded-lg" />
        </div>
        <Card className="px-4 py-2 flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total</span>
          <span className="font-display text-xl font-bold text-red-600 dark:text-red-400">{fmtMoney(total)}</span>
        </Card>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Date", "Vehicle", "Type", "Vendor", "Parts", "Labor", "Tax", "Total"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">No repairs match.</td></tr>}
            {filtered.map((r) => {
              const v = vehicles[r.vehicle_id];
              return (
                <tr key={r.id} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-4 py-3">{fmtDate(r.date || r.created_at)}</td>
                  <td className="px-4 py-3">{v ? <Link className="font-medium hover:text-blue-600" to={`/inventory/${v.id}`}>{v.year} {v.make} {v.model}</Link> : "—"}</td>
                  <td className="px-4 py-3 font-medium">{r.repair_type}</td>
                  <td className="px-4 py-3">{r.vendor || "—"}</td>
                  <td className="px-4 py-3">{fmtMoney(r.parts_cost)}</td>
                  <td className="px-4 py-3">{fmtMoney(r.labor_cost)}</td>
                  <td className="px-4 py-3">{fmtMoney(r.tax)}</td>
                  <td className="px-4 py-3 font-semibold">{fmtMoney(r.parts_cost + r.labor_cost + r.tax)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
